/**
 * CONTINUE BROWSING ("Verder waar je was gebleven") -- issue #477, CRO test 0168-26.
 *
 * Two jobs, one file, because they are two halves of one feature:
 *
 *   1. On a collection page, remember which collection this was (snippets/continue-browsing-record.liquid).
 *   2. On a page carrying sections/swb-continue-browsing.liquid, fetch that remembered collection's
 *      render of the very same section through the Section Rendering API and lift it into place.
 *
 * Step 2 exists because Liquid cannot know what the visitor saw last: that fact lives in the browser.
 * The theme already does exactly this round trip in its recently-viewed-products section, which
 * re-fetches itself against /search; this one re-fetches itself against /collections/<handle>.
 *
 * The wrapper starts out `hidden` and STAYS hidden unless real content arrives, so a visitor with no
 * history -- or whose last collection has since been emptied or deleted -- sees the page unchanged
 * rather than an empty block.
 *
 * It is deliberately hidden rather than REMOVED (issue #477). Removing it destroyed the block for the
 * rest of that page life, and a back-navigation restores a page from the bfcache with its DOM exactly
 * as it was left and WITHOUT re-running this script -- so a visitor who browsed a collection and then
 * pressed Back landed on a homepage whose block had already been deleted on the first visit, and could
 * never see it. Hiding costs nothing: assets/theme.css enforces `[data-continue-browsing][hidden]`.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'swb:last-viewed-collection';

  function remember() {
    var record = document.querySelector('[data-continue-browsing-record]');

    if (!record) {
      return;
    }

    var payload = {
      handle: record.getAttribute('data-handle'),
      title: record.getAttribute('data-title'),
      url: record.getAttribute('data-url')
    };

    if (!payload.handle || !payload.url) {
      return;
    }

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (error) {
      // A browser refusing storage (private mode, storage full, cookies blocked) is not an error
      // worth surfacing: the block simply never appears for that visitor.
    }
  }

  function lastViewedCollection() {
    var raw;

    try {
      raw = localStorage.getItem(STORAGE_KEY);
    } catch (error) {
      return null;
    }

    if (!raw) {
      return null;
    }

    try {
      var payload = JSON.parse(raw);
      return payload && payload.url ? payload : null;
    } catch (error) {
      return null;
    }
  }

  function sectionElement(wrapper) {
    return wrapper.classList.contains('shopify-section')
      ? wrapper
      : wrapper.closest('.shopify-section');
  }

  function sectionId(wrapper) {
    var section = sectionElement(wrapper);
    return section && section.id ? section.id.replace('shopify-section-', '') : null;
  }

  // NOT a remove -- see the note at the top of this file. `hidden` is restorable, removal is not.
  function hide(wrapper) {
    wrapper.setAttribute('hidden', '');
  }

  async function fill(wrapper) {
    var id = sectionId(wrapper);
    var last = lastViewedCollection();

    if (!id || !last) {
      hide(wrapper);
      return;
    }

    // The collection URL is market-aware already (Liquid wrote it), so it carries any locale prefix.
    var separator = last.url.indexOf('?') === -1 ? '?' : '&';
    var endpoint = last.url + separator + 'section_id=' + encodeURIComponent(id);
    var response;

    try {
      response = await fetch(endpoint, { priority: 'low' });
    } catch (error) {
      hide(wrapper);
      return;
    }

    // Belt and braces. Measured on the preview, a deleted or renamed collection does NOT answer 404
    // here: Shopify answers 200 with the section rendered against a blank `collection`, so it is the
    // empty-render check below that actually catches that case.
    if (!response.ok) {
      hide(wrapper);
      return;
    }

    var markup = new DOMParser().parseFromString(await response.text(), 'text/html');
    var rendered = markup.querySelector('continue-browsing');
    var host = wrapper.querySelector('continue-browsing');

    // An empty collection renders the section's `if` branch away, so there is nothing to lift.
    if (!rendered || rendered.childElementCount === 0 || !host) {
      hide(wrapper);
      return;
    }

    host.replaceChildren(...document.importNode(rendered, true).childNodes);
    wrapper.removeAttribute('hidden');
  }

  function load() {
    document.querySelectorAll('[data-continue-browsing]').forEach(fill);
  }

  remember();

  if ('requestIdleCallback' in window) {
    requestIdleCallback(load, { timeout: 1500 });
  } else {
    load();
  }

  // A bfcache restore replays no script, so without this the block keeps whatever state it was left in
  // -- which, for a visitor who arrives here by pressing Back, is the empty state of their first visit.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) {
      load();
    }
  });
})();
